/**
 * Finding photographs that are the same photograph.
 *
 * Byte-identical copies are the easy half and the server already refuses them
 * within a group. What actually fills a disk is the other kind: thirty frames
 * of the same jump, four attempts at the same group photo, the same picture
 * once from the camera and once after a messaging app re-compressed it. Nothing
 * about those is identical, and every one of them is a duplicate to a person.
 *
 * A difference hash answers that. Shrink the picture until only its large-scale
 * structure survives, record which way the brightness steps between neighbouring
 * pixels, and two photographs of the same scene agree on nearly every step
 * regardless of exposure, scale, compression or a watermark in the corner.
 *
 * All of it is pure and none of it touches the DOM — the caller decodes, this
 * decides. That is not tidiness: "are these two the same picture" is a question
 * with a right answer, and a right answer is worth testing.
 */

/** Rec. 709, the same weights the editor uses. */
export function toGrayscale(rgba: Uint8ClampedArray): Uint8Array {
  const gray = new Uint8Array(rgba.length / 4);
  for (let index = 0, out = 0; index < rgba.length; index += 4, out++) {
    gray[out] = Math.round(
      rgba[index]! * 0.2126 +
        rgba[index + 1]! * 0.7152 +
        rgba[index + 2]! * 0.0722,
    );
  }
  return gray;
}

/**
 * Box-filter down to a target size.
 *
 * Averaging rather than sampling, which matters more than it sounds: picking
 * one pixel out of each block makes the hash depend on noise and on JPEG block
 * boundaries, so the same photograph saved twice at different qualities hashes
 * differently — the exact case this is supposed to catch.
 */
export function downsample(
  gray: Uint8Array,
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number,
): Uint8Array {
  const out = new Uint8Array(targetWidth * targetHeight);

  for (let ty = 0; ty < targetHeight; ty++) {
    const y0 = Math.floor((ty * height) / targetHeight);
    const y1 = Math.max(y0 + 1, Math.floor(((ty + 1) * height) / targetHeight));

    for (let tx = 0; tx < targetWidth; tx++) {
      const x0 = Math.floor((tx * width) / targetWidth);
      const x1 = Math.max(x0 + 1, Math.floor(((tx + 1) * width) / targetWidth));

      let total = 0;
      let count = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          total += gray[y * width + x] ?? 0;
          count++;
        }
      }
      out[ty * targetWidth + tx] = count === 0 ? 0 : Math.round(total / count);
    }
  }

  return out;
}

/** The hash is 8×8 comparisons, so the grid it reads is nine wide. */
export const HASH_WIDTH = 9;
export const HASH_HEIGHT = 8;

/**
 * The difference hash of an already-shrunk 9×8 grid.
 *
 * One bit per horizontal step: is this pixel brighter than the one to its
 * right. Relative, never absolute, which is why a photograph and the same
 * photograph two stops darker hash identically.
 */
export function dhash(grid: Uint8Array): bigint {
  let hash = 0n;
  for (let y = 0; y < HASH_HEIGHT; y++) {
    for (let x = 0; x < HASH_WIDTH - 1; x++) {
      const left = grid[y * HASH_WIDTH + x] ?? 0;
      const right = grid[y * HASH_WIDTH + x + 1] ?? 0;
      hash = (hash << 1n) | (left > right ? 1n : 0n);
    }
  }
  return hash;
}

/* ──────────────────────────── Colour ─────────────────────────────────────── */

/**
 * A coarse colour fingerprint, as chromaticity rather than as brightness.
 *
 * A difference hash reads structure and nothing else, which is its strength —
 * and, on its own, a serious hazard. A photograph with no structure hashes to
 * all zeroes, so *every* flat picture is identical to every other one: a black
 * pocket shot, a blue sky and a white wall come out as the same photograph and
 * two of the three get offered up for deletion.
 *
 * Chromaticity — each channel as a fraction of the total — separates them,
 * while staying invariant to exposure, which is the property the hash was
 * chosen for in the first place. The same picture two stops darker has the same
 * chromaticity; a red wall and a blue sky do not.
 */
export const CHROMA_CELLS = 2;

export function chromaSignature(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): number[] {
  const signature: number[] = [];

  for (let cy = 0; cy < CHROMA_CELLS; cy++) {
    for (let cx = 0; cx < CHROMA_CELLS; cx++) {
      const x0 = Math.floor((cx * width) / CHROMA_CELLS);
      const x1 = Math.max(
        x0 + 1,
        Math.floor(((cx + 1) * width) / CHROMA_CELLS),
      );
      const y0 = Math.floor((cy * height) / CHROMA_CELLS);
      const y1 = Math.max(
        y0 + 1,
        Math.floor(((cy + 1) * height) / CHROMA_CELLS),
      );

      let red = 0;
      let green = 0;
      let blue = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const at = (y * width + x) * 4;
          red += rgba[at] ?? 0;
          green += rgba[at + 1] ?? 0;
          blue += rgba[at + 2] ?? 0;
        }
      }

      const total = red + green + blue;
      // Pure black has no hue at all; call it neutral rather than dividing by
      // zero and producing NaN, which would poison every comparison after it.
      signature.push(
        total === 0 ? 1 / 3 : red / total,
        total === 0 ? 1 / 3 : green / total,
      );
    }
  }

  return signature;
}

/** The largest disagreement between two signatures, on a 0..1 scale. */
export function chromaDistance(a: number[], b: number[]): number {
  let worst = 0;
  for (let index = 0; index < Math.max(a.length, b.length); index++) {
    worst = Math.max(worst, Math.abs((a[index] ?? 0) - (b[index] ?? 0)));
  }
  return worst;
}

/**
 * How far two pictures may differ in hue and still be the same picture.
 *
 * Generous, because it is only ever a veto on a structural match: JPEG at a low
 * quality shifts colour a little, and a screenshot of a photograph shifts it
 * more. Its job is to separate a blue frame from a red one, not to grade colour.
 */
export const CHROMA_TOLERANCE = 0.06;

/* ─────────────────────────── How much there is ───────────────────────────── */

/**
 * How much real structure the hash was taken from, 0..1.
 *
 * Every bit of a difference hash is one comparison — is this cell brighter than
 * its neighbour — and a comparison between two nearly equal cells is a coin
 * toss recorded as a fact. A photograph of a room decides almost every one of
 * its sixty-four comparisons decisively. A screenshot of a game, a document, a
 * user interface, anything with large flat areas, decides most of them by a
 * rounding error.
 *
 * This matters because two such hashes agreeing means much less than two rich
 * ones agreeing, and the grouping had no way to know that: four screenshots of
 * different moments in the same game share a layout and a palette, so their
 * near-empty hashes land close together and the scan called them one moment.
 */
export function structure(grid: Uint8Array): number {
  let decisive = 0;
  let total = 0;

  for (let y = 0; y < HASH_HEIGHT; y++) {
    for (let x = 0; x < HASH_WIDTH - 1; x++) {
      const left = grid[y * HASH_WIDTH + x] ?? 0;
      const right = grid[y * HASH_WIDTH + x + 1] ?? 0;
      // Eight levels out of 255: below this the comparison is noise, and JPEG
      // alone can flip it.
      if (Math.abs(left - right) > 8) decisive++;
      total++;
    }
  }

  return total === 0 ? 0 : decisive / total;
}

/** Below this, a hash is mostly coin tosses and deserves a stricter match. */
export const LOW_STRUCTURE = 0.55;

/** Decode, grayscale, shrink, hash — the whole pipeline for one picture. */
export function hashPixels(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): bigint {
  const gray = toGrayscale(rgba);
  return dhash(downsample(gray, width, height, HASH_WIDTH, HASH_HEIGHT));
}

/** The hash and how much it is worth, from one pass over the pixels. */
export function measurePixels(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): { hash: bigint; structure: number } {
  const grid = downsample(
    toGrayscale(rgba),
    width,
    height,
    HASH_WIDTH,
    HASH_HEIGHT,
  );
  return { hash: dhash(grid), structure: structure(grid) };
}

/** How many of the sixty-four judgements the two pictures disagree on. */
export function hamming(a: bigint, b: bigint): number {
  let difference = a ^ b;
  let count = 0;
  while (difference > 0n) {
    difference &= difference - 1n;
    count++;
  }
  return count;
}

/**
 * Distances, and what they mean.
 *
 * Two different questions get two different answers, because the useful actions
 * are different. "These are the same photograph twice" invites deleting one
 * without looking. "These are the same moment" invites *looking* — the whole
 * point of six near-identical group selfies is that in one of them everybody
 * has their eyes open, and only a person can say which.
 *
 * So the tighter band pre-selects copies for the bin, and the looser one only
 * ever puts pictures side by side. Nothing in the looser band is ever ticked by
 * default.
 */
export const IDENTICAL_WITHIN = 2;
export const DUPLICATE_WITHIN = 6;

/** Structurally close, at any distance in time. */
export const SIMILAR_WITHIN = 10;

/**
 * Structurally close *and* taken moments apart, which earns a looser bar.
 *
 * Time is the strongest signal available here and it costs nothing. Two frames
 * a second apart that are roughly alike are the same moment, essentially
 * always; two frames a year apart that are roughly alike are usually two
 * pictures of the same building. The same hash distance means different things
 * and should not be treated the same way.
 */
export const BURST_SIMILAR_WITHIN = 16;
export const BURST_WINDOW_MS = 2 * 60_000;

/**
 * Colour has to agree for a duplicate, and merely not disagree for a moment.
 * The tight one lives up in the colour section, beside what produces it.
 */
export const CHROMA_TOLERANCE_SIMILAR = 0.12;

export interface Hashed {
  id: string;
  hash: bigint;
  /** Chromaticity, which vetoes a structural match between different colours. */
  chroma: number[];
  /** Milliseconds. Orders a set, and decides whether two frames are one moment. */
  takenAt: number;
  /** How much the hash was worth. See `structure`. Absent means "assume rich". */
  structure?: number;
}

export type Relation = "duplicate" | "similar";

/**
 * How two photographs are related, if at all.
 *
 * A duplicate needs structure and colour to agree closely. A moment needs them
 * to agree loosely, and gets more latitude on structure when the timestamps say
 * the shutter was pressed twice in a few seconds.
 */
export function related(a: Hashed, b: Hashed): Relation | null {
  const distance = hamming(a.hash, b.hash);
  const colour = chromaDistance(a.chroma, b.chroma);

  if (distance <= DUPLICATE_WITHIN && colour <= CHROMA_TOLERANCE)
    return "duplicate";

  // Still the colour veto, just a wider one — the same room at two exposures is
  // one moment, a red wall and a blue sky are not.
  if (colour > CHROMA_TOLERANCE_SIMILAR) return null;

  const sameMoment = Math.abs(a.takenAt - b.takenAt) <= BURST_WINDOW_MS;
  let limit = sameMoment ? BURST_SIMILAR_WITHIN : SIMILAR_WITHIN;

  /*
   * A picture with little structure gets a stricter bar.
   *
   * Most of its hash is comparisons between nearly equal cells, which are coin
   * tosses recorded as facts, so two such hashes landing close together is
   * weak evidence. Four screenshots of different moments in one game share a
   * layout and a palette and were being called a single moment on the strength
   * of it. Halving the allowance asks them to agree on the bits that actually
   * carry something.
   */
  const poorest = Math.min(a.structure ?? 1, b.structure ?? 1);
  if (poorest < LOW_STRUCTURE) limit = Math.floor(limit / 2);

  return distance <= limit ? "similar" : null;
}

/** Kept for callers that want one specific band. */
export function alike(a: Hashed, b: Hashed, threshold: number): boolean {
  return (
    hamming(a.hash, b.hash) <= threshold &&
    chromaDistance(a.chroma, b.chroma) <= CHROMA_TOLERANCE
  );
}

export interface RelatedSet {
  ids: string[];
  /** What the set as a whole is: copies, or one moment shot several times. */
  relation: Relation;
  /** The largest distance inside the set, so the interface can say how sure. */
  spread: number;
  /**
   * The members that are a true copy of an earlier member.
   *
   * These, and only these, are safe to tick by default. A set of six shots of
   * one moment that happens to contain two identical frames offers those two
   * and leaves the other four alone.
   */
  redundant: string[];
}

/** Single-link union-find over a predicate. Shared by both passes. */
function cluster(
  size: number,
  connected: (a: number, b: number) => boolean,
): number[][] {
  const parent = Array.from({ length: size }, (_, index) => index);

  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root]!;
    // Path compression, so a long burst does not become a long walk.
    let walk = index;
    while (parent[walk] !== root) {
      const next = parent[walk]!;
      parent[walk] = root;
      walk = next;
    }
    return root;
  };

  for (let a = 0; a < size; a++) {
    for (let b = a + 1; b < size; b++) {
      if (!connected(a, b)) continue;
      const rootA = find(a);
      const rootB = find(b);
      if (rootA !== rootB) parent[rootB] = rootA;
    }
  }

  const buckets = new Map<number, number[]>();
  for (let index = 0; index < size; index++) {
    const root = find(index);
    const bucket = buckets.get(root) ?? [];
    bucket.push(index);
    buckets.set(root, bucket);
  }
  return [...buckets.values()];
}

/**
 * Gather photographs into sets, labelled by what they are.
 *
 * Single-link: A joins B's set if it is close to *any* member, which is what
 * makes a burst come out as one set rather than a chain of overlapping pairs.
 * The risk of single-link is drift — A near B near C, with A and C unrelated —
 * so the set's real spread is reported, and a set that has drifted at all is
 * labelled a moment rather than a copy, which means nothing in it is ticked.
 */
export function groupRelated(entries: Hashed[]): RelatedSet[] {
  const sets = cluster(
    entries.length,
    (a, b) => related(entries[a]!, entries[b]!) !== null,
  );
  const out: RelatedSet[] = [];

  for (const members of sets) {
    // A set of one is not a set.
    if (members.length < 2) continue;

    let spread = 0;
    for (let a = 0; a < members.length; a++) {
      for (let b = a + 1; b < members.length; b++) {
        spread = Math.max(
          spread,
          hamming(entries[members[a]!]!.hash, entries[members[b]!]!.hash),
        );
      }
    }

    const ordered = members
      .map((index) => entries[index]!)
      .sort((x, y) => x.takenAt - y.takenAt);

    /*
     * A second pass at the tight threshold, inside the set. Everything after
     * the first of each true-copy run is redundant; everything else is a
     * distinct photograph of the same moment and is nobody's business but the
     * person looking at it.
     */
    const redundant: string[] = [];
    for (const run of cluster(ordered.length, (a, b) =>
      alike(ordered[a]!, ordered[b]!, DUPLICATE_WITHIN),
    )) {
      if (run.length < 2) continue;
      for (const index of run.slice(1).sort((x, y) => x - y)) {
        redundant.push(ordered[index]!.id);
      }
    }

    out.push({
      ids: ordered.map((entry) => entry.id),
      relation: spread <= DUPLICATE_WITHIN ? "duplicate" : "similar",
      spread,
      redundant,
    });
  }

  /*
   * Copies first, then moments; within each, the biggest set first. Copies are
   * the part with an obvious action, so they belong where somebody will act on
   * them before their patience runs out.
   */
  return out.sort(
    (a, b) =>
      Number(b.relation === "duplicate") - Number(a.relation === "duplicate") ||
      b.ids.length - a.ids.length,
  );
}

/** How many files could be removed without a person having to choose. */
export function reclaimable(sets: RelatedSet[]): number {
  return sets.reduce((total, set) => total + set.redundant.length, 0);
}
